const STAGES = [
  {
    id: "inspiration",
    label: "Inspiration",
    short: "Intent, references and the feeling the world should create.",
    output: "Approved vision",
  },
  {
    id: "world_plan",
    label: "World Plan",
    short: "Bounds, terrain logic, areas, routes and landmarks.",
    output: "Approved structure",
  },
  {
    id: "greybox",
    label: "Greybox",
    short: "Playable scale, sightlines, traversal and spatial revisions.",
    output: "Approved greybox",
  },
  {
    id: "art_direction",
    label: "Art Direction",
    short: "Shape language, kits, materials, palette and lighting.",
    output: "Approved art bible",
  },
  {
    id: "production",
    label: "Production",
    short: "Assets, terrain, dressing and production review.",
    output: "Production baseline",
  },
];

const STAGE_ALIASES = {
  brief: "inspiration",
  concept: "inspiration",
  planning: "world_plan",
  world: "world_plan",
  layout: "world_plan",
  blockout: "greybox",
  art: "art_direction",
  dressing: "production",
  assets: "production",
};

function projectId(project) {
  return project?.id ?? project?.projectId ?? "";
}

function stageId(value) {
  const normalized = String(value || "inspiration").trim().toLowerCase().replace(/[ -]+/g, "_");
  return STAGES.some((stage) => stage.id === normalized) ? normalized : STAGE_ALIASES[normalized] ?? "inspiration";
}

function projectName(project) {
  return project?.name ?? project?.title ?? "Untitled world";
}

function artifactKindForStage(stage) {
  return ({ inspiration: "world_direction", world_plan: "world_plan", art_direction: "art_bible" })[stage] || "";
}

export function initStudio({ api, toast, esc, navigate, hrefFor = null }) {
  const root = document.getElementById("panel");
  const projectSelect = document.getElementById("project-select");
  const needsButton = document.getElementById("needs-you-btn");
  const needsCount = document.getElementById("needs-you-count");

  let projects = [];
  let selectedId = localStorage.getItem("wl_project") || "";
  let selectedProject = null;
  let detail = null;
  let loading = false;
  let loadError = "";
  let creating = false;

  function setSelected(id) {
    selectedId = id || "";
    selectedProject = projects.find((project) => projectId(project) === selectedId) ?? null;
    if (selectedId) localStorage.setItem("wl_project", selectedId);
    else localStorage.removeItem("wl_project");
    document.body.dataset.projectId = selectedId;
    window.dispatchEvent(new CustomEvent("worldloom:project-change", { detail: { projectId: selectedId, project: selectedProject } }));
  }

  function populateProjectSelect() {
    projectSelect.disabled = false;
    projectSelect.innerHTML = projects.length
      ? `${projects.map((project) => `<option value="${esc(projectId(project))}">${esc(projectName(project))}</option>`).join("")}<option value="__new__">＋ New project…</option>`
      : `<option value="">No project yet</option><option value="__new__">＋ New project…</option>`;
    projectSelect.value = selectedProject ? selectedId : "";
  }

  async function updateNeedsCount() {
    try {
      if (!selectedId) return;
      const [{ tickets = [] }, { reviews = [] }] = await Promise.all([
        api(`/tickets?projectId=${encodeURIComponent(selectedId)}`),
        api(`/projects/${encodeURIComponent(selectedId)}/reviews`),
      ]);
      const hasProjectScope = tickets.some((ticket) => ticket.projectId || ticket.project_id);
      const review = tickets.filter((ticket) => {
        if (ticket.status !== "review") return false;
        if (!hasProjectScope) return false;
        return (ticket.projectId ?? ticket.project_id) === selectedId;
      }).length;
      const count = review + reviews.length;
      needsCount.hidden = count === 0;
      needsCount.textContent = String(count);
      needsButton.classList.toggle("has-items", count > 0);
      needsButton.setAttribute("aria-label", count ? `Needs you, ${count} review${count === 1 ? "" : "s"}` : "Needs you");
    } catch {
      needsCount.hidden = true;
      needsButton.classList.remove("has-items");
    }
  }

  async function loadProjectDetail() {
    detail = null;
    if (!selectedId) return;
    try {
      detail = await api(`/projects/${encodeURIComponent(selectedId)}`);
      if (detail?.project) {
        selectedProject = detail.project;
        const index = projects.findIndex((project) => projectId(project) === selectedId);
        if (index >= 0) projects[index] = detail.project;
      }
    } catch (error) {
      loadError = `The project exists, but its workspace could not be loaded. ${String(error)}`;
    }
  }

  async function refreshProjects(preferredId = "") {
    loading = true;
    loadError = "";
    if (root && document.body.dataset.view === "studio") renderLoading();
    try {
      const result = await api("/projects");
      projects = Array.isArray(result?.projects) ? result.projects : [];
      const candidate = preferredId || selectedId;
      const next = projects.find((project) => projectId(project) === candidate) ?? projects[0] ?? null;
      setSelected(projectId(next));
      await loadProjectDetail();
      populateProjectSelect();
      await updateNeedsCount();
    } catch (error) {
      projects = [];
      setSelected("");
      projectSelect.innerHTML = `<option>Projects unavailable</option>`;
      projectSelect.disabled = true;
      loadError = String(error);
    } finally {
      loading = false;
      if (document.body.dataset.view === "studio") render();
    }
  }

  function renderLoading() {
    root.innerHTML = `
      <div class="studio-shell studio-loading" aria-busy="true">
        <div class="studio-loading-line"></div>
        <div class="studio-loading-title"></div>
        <div class="studio-loading-grid">
          <div></div><div></div><div></div>
        </div>
        <p>Opening the project workspace…</p>
      </div>`;
  }

  function renderCreateProject() {
    root.innerHTML = `
      <div class="studio-shell studio-create-shell">
        <section class="studio-create-card" aria-labelledby="new-project-title">
          <button class="studio-back" type="button" data-studio-action="cancel-create">← Back to projects</button>
          <div class="studio-kicker">New world project</div>
          <h1 id="new-project-title">Start with intent,<br>not a ticket.</h1>
          <p class="studio-lede">Give the project a stable identity and a useful creative brief. Inspiration, greybox revisions, art direction and every generated asset will stay inside this boundary.</p>
          <form id="studio-create-form">
            <label>
              Project name
              <input name="name" type="text" autocomplete="off" maxlength="100" placeholder="The Shattered Coast" required autofocus>
            </label>
            <label>
              World brief <span>optional — type or dictate</span>
              <textarea name="brief" rows="6" maxlength="5000" placeholder="A windswept pilgrimage route crossing a drowned industrial coastline. Movement should feel exposed above ground and compressed inside the ruins…"></textarea>
            </label>
            <div class="studio-form-actions">
              <button class="primary studio-primary" type="submit">Create project</button>
              <p>Creates an isolated workspace. Nothing is generated or billed yet.</p>
            </div>
          </form>
        </section>
        <aside class="studio-create-aside" aria-label="Project workflow">
          <span class="studio-map-index">01—05</span>
          <h2>One world.<br>Five controlled stages.</h2>
          <ol>
            ${STAGES.map((stage, index) => `<li><span>0${index + 1}</span><b>${esc(stage.label)}</b><small>${esc(stage.output)}</small></li>`).join("")}
          </ol>
        </aside>
      </div>`;
    bindStudioActions();
  }

  function renderEmpty() {
    root.innerHTML = `
      <div class="studio-shell studio-empty-shell">
        <section class="studio-empty">
          <div class="studio-kicker">World production starts here</div>
          <h1>Build the world,<br><em>decision by decision.</em></h1>
          <p>Shape the vision, plan the terrain, walk the greybox, approve the art direction, then let production agents scale the approved result.</p>
          <button class="primary studio-primary" type="button" data-studio-action="new-project">Create your first project</button>
          <span class="studio-trust-note">Projects keep references, revisions, assets and costs completely separate.</span>
        </section>
        <div class="studio-contour" aria-hidden="true">
          <span class="contour-label north">VISION</span>
          <span class="contour-label center">GREYBOX</span>
          <span class="contour-label south">PRODUCTION</span>
          <div class="contour-pin"></div>
        </div>
      </div>`;
    bindStudioActions();
  }

  function renderUnavailable() {
    root.innerHTML = `
      <div class="studio-shell studio-empty-shell">
        <section class="studio-empty studio-error-state">
          <div class="studio-kicker">Project workspace unavailable</div>
          <h1>The studio shell is ready.<br><em>The project service is not.</em></h1>
          <p>No temporary browser project was created. Connect the project backend, then retry so revisions and assets have one trustworthy home.</p>
          <button type="button" data-studio-action="retry">Retry connection</button>
          <details><summary>Technical detail</summary><pre>${esc(loadError)}</pre></details>
        </section>
      </div>`;
    bindStudioActions();
  }

  function renderStageRail(activeId) {
    const activeIndex = Math.max(0, STAGES.findIndex((stage) => stage.id === activeId));
    return `
      <ol class="studio-stage-rail" aria-label="World production stages">
        ${STAGES.map((stage, index) => {
          const state = index < activeIndex ? "complete" : index === activeIndex ? "current" : "locked";
          return `<li class="${state}" ${state === "current" ? `aria-current="step"` : ""}>
            <span class="stage-number">0${index + 1}</span>
            <span class="stage-copy"><b>${esc(stage.label)}</b><small>${state === "complete" ? esc(stage.output) : esc(stage.short)}</small></span>
            <span class="stage-state">${state === "complete" ? "Approved" : state === "current" ? "Now" : "Later"}</span>
          </li>`;
        }).join("")}
      </ol>`;
  }

  function renderProject() {
    const activeId = stageId(selectedProject?.currentStage);
    const active = STAGES.find((stage) => stage.id === activeId) ?? STAGES[0];
    const maps = Array.isArray(detail?.maps) ? detail.maps : [];
    const artifacts = Array.isArray(detail?.artifacts) ? detail.artifacts : [];
    const mapCount = maps.length;
    const artifactCount = artifacts.length;
    const canOpenWorld = ["greybox", "art_direction", "production"].includes(activeId) && mapCount > 0;

    root.innerHTML = `
      <div class="studio-shell studio-project-shell">
        <header class="studio-project-head">
          <div>
            <div class="studio-kicker">Current project · ${esc(selectedProject?.role ?? "workspace")}</div>
            <h1>${esc(projectName(selectedProject))}</h1>
          </div>
          <button type="button" data-studio-action="new-project">＋ New project</button>
        </header>

        ${loadError ? `<div class="studio-inline-warning" role="status"><b>Project details are unavailable.</b><span>${esc(loadError)}</span><button type="button" data-studio-action="retry">Retry</button></div>` : ""}

        ${renderStageRail(activeId)}

        <div class="studio-work-grid">
          <section class="studio-next-card">
            <div class="studio-card-index">NEXT / 0${STAGES.findIndex((stage) => stage.id === activeId) + 1}</div>
            <div class="studio-next-copy">
              <span class="studio-stage-chip">${esc(active.label)}</span>
              <h2>${activeId === "inspiration" ? "Give the world a point of view." : activeId === "world_plan" ? "Turn intent into spatial logic." : activeId === "greybox" ? "Walk it before you decorate it." : activeId === "art_direction" ? "Define the rules before scaling production." : "Produce from approved decisions."}</h2>
              <p>${esc(active.short)}</p>
            </div>
            <div class="studio-next-actions">
              ${canOpenWorld ? `<a class="primary studio-primary studio-route-link" href="${esc(hrefFor?.("level") || "#")}" data-navigate="level">Open World</a>` : `<button class="primary studio-primary" type="button" data-studio-action="stage-note">Continue ${esc(active.label)}</button>`}
              <span>${canOpenWorld ? `${mapCount || 1} world map${mapCount === 1 ? "" : "s"} available` : "No generation starts without an explicit action."}</span>
            </div>
          </section>

          <aside class="studio-pulse-card">
            <div class="studio-card-index">PROJECT PULSE</div>
            <dl>
              <div><dt>World maps</dt><dd>${mapCount}</dd></div>
              <div><dt>Tracked artifacts</dt><dd>${artifactCount}</dd></div>
              <div><dt>Current gate</dt><dd>${esc(active.output)}</dd></div>
            </dl>
            <a class="studio-route-link" href="${esc(hrefFor?.("tickets") || "#")}" data-navigate="tickets">View production board →</a>
          </aside>
        </div>

        <section class="studio-stage-detail" id="studio-stage-detail">
          <div>
            <div class="studio-card-index">STAGE BRIEF</div>
            <h2>${esc(active.label)}</h2>
            <p>${esc(active.short)}</p>
          </div>
          <div class="studio-task-list">
            ${activeId === "inspiration" ? `
              <article><span>01</span><div><b>World intent</b><p>Experience, scale, traversal and emotional target.</p></div><small>Next</small></article>
              <article><span>02</span><div><b>Reference directions</b><p>Images, sketches, notes and explicit avoidances.</p></div><small>Waiting</small></article>
              <article><span>03</span><div><b>Vision gate</b><p>Approve one direction before spatial planning.</p></div><small>Locked</small></article>` : `
              <article><span>01</span><div><b>Review approved inputs</b><p>Every new revision stays linked to the decisions that produced it.</p></div><small>Ready</small></article>
              <article><span>02</span><div><b>Create candidate revision</b><p>Agent and human work use the same reviewable artifact contract.</p></div><small>Next</small></article>
              <article><span>03</span><div><b>${esc(active.output)}</b><p>Only an approved candidate becomes the project baseline.</p></div><small>Gate</small></article>`}
          </div>
          ${renderStageComposer(activeId, artifacts, maps)}
          ${renderMapDesignChange(artifacts, maps)}
        </section>
      </div>`;
    bindStudioActions();
  }

  function renderStageComposer(activeId, artifacts, maps) {
    if (activeId === "greybox") {
      return `<div class="studio-stage-composer"><div class="studio-card-index">GENERATE</div><h3>Create the first spatial candidate</h3><p>The approved World Plan becomes a section graph, then a structural Three.js greybox. No dressing or final assets will start.</p>
        <button class="primary studio-primary" type="button" data-studio-action="generate-greybox" ${maps.length ? "" : "disabled"}>Generate first greybox</button></div>`;
    }
    if (activeId === "production") {
      const artBible = artifacts.find((artifact) => artifact.kind === "art_bible" && artifact.approvedRevisionId);
      const greybox = artifacts.find((artifact) => artifact.kind === "greybox" && artifact.approvedRevisionId);
      return `<div class="studio-stage-composer"><div class="studio-card-index">SECTION DRESSING</div><h3>Place approved assets in the world</h3><p>Creates one visible area ticket per section. Agents reuse the approved catalog first; only portable prop gaps can start image-to-3D. Architecture and oversized gaps remain unplaced and are disclosed in review for explicit Structures routing.</p>
        <button class="primary studio-primary" type="button" data-studio-action="start-production" ${artBible && greybox ? "" : "disabled"}>Start prop dressing pass</button>
        ${artBible && greybox ? "" : "<span>Approve both the greybox and art direction first.</span>"}</div>`;
    }
    const kind = artifactKindForStage(activeId);
    if (!kind) return "";
    const existing = artifacts.find((artifact) => artifact.kind === kind);
    const heading = activeId === "inspiration" ? "Propose a world direction" : activeId === "world_plan" ? "Define the world plan" : "Propose the art direction";
    const placeholder = activeId === "inspiration"
      ? "Describe the emotional target, player experience, visual pillars and explicit avoidances…"
      : activeId === "world_plan"
        ? "Describe the bounds, areas, terrain logic, routes, landmarks, verticality and traversal assumptions…"
        : "Describe shape language, architecture rules, materials, palette, lighting, atmosphere and forbidden traits…";
    return `<form class="studio-stage-composer" id="studio-candidate-form" data-artifact-kind="${esc(kind)}" data-artifact-id="${esc(existing?.id || "")}">
      <div class="studio-card-index">CANDIDATE REVISION</div><h3>${esc(heading)}</h3><p>Human and agent work enter the same immutable review chain. Approval—not generation—changes the project baseline.</p>
      <textarea name="summary" rows="5" maxlength="8000" placeholder="${esc(placeholder)}" required></textarea>
      ${activeId === "world_plan" ? `<div class="studio-plan-fields"><label>Width (m)<input name="widthM" type="number" min="10" max="10000" value="200"></label><label>Depth (m)<input name="depthM" type="number" min="10" max="10000" value="200"></label><label>Sections<input name="maxSections" type="number" min="1" max="12" value="5"></label></div>` : ""}
      <div class="studio-form-actions"><button class="primary studio-primary" type="submit">Submit candidate for review</button><span>${existing ? "Creates the next revision; the approved version remains current." : "Creates the first candidate for this stage."}</span></div>
    </form>`;
  }

  function renderMapDesignChange(artifacts, maps) {
    const map = maps[0];
    if (!map) return "";
    const design = artifacts.find((artifact) => artifact.kind === "map_design");
    return `<form class="studio-stage-composer" id="studio-map-design-change-form" data-map-id="${esc(map.worldId)}">
      <div class="studio-card-index">ITERATIVE MAP DESIGN</div>
      <h3>Add or change one design decision</h3>
      <p>The designer agent applies this instruction as a stable-ID patch to the current full map state. Unmentioned requirements remain unchanged, and the result becomes a new reviewable revision.</p>
      <textarea name="instruction" rows="4" maxlength="10000" placeholder="Make the map circular with a 1.5 km radius, two concentric regions, and a ring road between them…" required></textarea>
      <div class="studio-form-actions"><button class="primary studio-primary" type="submit">Queue design revision</button><span>${design ? "Builds on the current map-design revision." : "Creates the first structured map-design revision."}</span></div>
    </form>`;
  }

  function render() {
    if (loading) return renderLoading();
    if (creating) return renderCreateProject();
    if (loadError && !projects.length) return renderUnavailable();
    if (!selectedProject) return renderEmpty();
    renderProject();
  }

  function bindStudioActions() {
    root.querySelectorAll("[data-navigate]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (event.button !== 0 || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(link.dataset.navigate);
      });
    });
    root.querySelectorAll("[data-studio-action]").forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.studioAction;
        if (action === "new-project") {
          creating = true;
          projectSelect.value = "__new__";
          render();
        } else if (action === "cancel-create") {
          creating = false;
          populateProjectSelect();
          render();
        } else if (action === "retry") {
          refreshProjects();
        } else if (action === "stage-note") {
          document.getElementById("studio-stage-detail")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    });
    const form = root.querySelector("#studio-create-form");
    if (form) {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const submit = form.querySelector("button[type='submit']");
        const data = new FormData(form);
        const name = String(data.get("name") || "").trim();
        const brief = String(data.get("brief") || "").trim();
        if (!name) return;
        submit.disabled = true;
        submit.textContent = "Creating project…";
        try {
          const result = await api("/projects", { method: "POST", body: JSON.stringify({ name, ...(brief ? { brief } : {}) }) });
          creating = false;
          await refreshProjects(projectId(result?.project));
          toast(`${name} is ready`);
        } catch (error) {
          submit.disabled = false;
          submit.textContent = "Create project";
          toast(`Could not create project: ${String(error)}`, true);
        }
      });
    }
    const candidateForm = root.querySelector("#studio-candidate-form");
    if (candidateForm) {
      candidateForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const submit = candidateForm.querySelector("button[type='submit']");
        const data = new FormData(candidateForm);
        const summary = String(data.get("summary") || "").trim();
        if (!summary || !selectedId) return;
        submit.disabled = true;
        submit.textContent = "Creating revision…";
        try {
          const kind = candidateForm.dataset.artifactKind;
          let artifactId = candidateForm.dataset.artifactId;
          const mapId = kind === "world_plan" ? detail?.maps?.[0]?.worldId : null;
          if (!artifactId) {
            const created = await api(`/projects/${encodeURIComponent(selectedId)}/artifacts`, {
              method: "POST",
              body: JSON.stringify({ kind, name: kind === "world_direction" ? "World direction" : kind === "world_plan" ? "World plan" : "Art bible", ...(mapId ? { mapId } : {}) }),
            });
            artifactId = created.artifact.id;
          }
          const body = { summary };
          if (kind === "world_plan") {
            body.worldSizeM = { x: Number(data.get("widthM") || 200), y: Number(data.get("depthM") || 200) };
            body.maxSections = Number(data.get("maxSections") || 5);
          }
          const upstreamKind = kind === "world_plan" ? "world_direction" : kind === "art_bible" ? "greybox" : null;
          const upstream = upstreamKind
            ? detail?.artifacts?.find((candidate) => candidate.kind === upstreamKind && candidate.approvedRevisionId)
            : null;
          const createdRevision = await api(`/projects/${encodeURIComponent(selectedId)}/artifacts/${encodeURIComponent(artifactId)}/revisions`, {
            method: "POST",
            body: JSON.stringify({
              body,
              changeNote: summary.slice(0, 500),
              inputRevisionIds: upstream?.approvedRevisionId ? [upstream.approvedRevisionId] : [],
            }),
          });
          await api(`/projects/${encodeURIComponent(selectedId)}/artifacts/${encodeURIComponent(artifactId)}/revisions/${encodeURIComponent(createdRevision.revision.id)}/submit`, { method: "POST" });
          toast("Candidate submitted to Needs you");
          await loadProjectDetail();
          await updateNeedsCount();
          render();
        } catch (error) {
          submit.disabled = false;
          submit.textContent = "Submit candidate for review";
          toast(`Could not create revision: ${String(error)}`, true);
        }
      });
    }
    const mapDesignForm = root.querySelector("#studio-map-design-change-form");
    if (mapDesignForm) {
      mapDesignForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const submit = mapDesignForm.querySelector("button[type='submit']");
        const instruction = String(new FormData(mapDesignForm).get("instruction") || "").trim();
        const mapId = mapDesignForm.dataset.mapId;
        if (!instruction || !selectedId || !mapId) return;
        submit.disabled = true;
        submit.textContent = "Queueing revision…";
        try {
          await api(`/projects/${encodeURIComponent(selectedId)}/maps/${encodeURIComponent(mapId)}/design-changes`, {
            method: "POST",
            body: JSON.stringify({ instruction }),
          });
          mapDesignForm.reset();
          toast("Map-design revision queued");
          await loadProjectDetail();
          await updateNeedsCount();
          render();
        } catch (error) {
          submit.disabled = false;
          submit.textContent = "Queue design revision";
          toast(`Could not queue map-design revision: ${String(error)}`, true);
        }
      });
    }
    root.querySelector('[data-studio-action="generate-greybox"]')?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const map = detail?.maps?.[0];
      const plan = detail?.artifacts?.find((artifact) => artifact.kind === "world_plan" && artifact.approvedRevisionId);
      if (!map || !plan) return toast("Approve a World Plan before generating the greybox", true);
      button.disabled = true;
      button.textContent = "Queueing greybox…";
      try {
        const artifactDetail = await api(`/projects/${encodeURIComponent(selectedId)}/artifacts/${encodeURIComponent(plan.id)}`);
        const approved = artifactDetail.revisions.find((revision) => revision.id === plan.approvedRevisionId);
        const planBody = approved?.body || {};
        const size = planBody.worldSizeM || { x: 200, y: 200 };
        await api("/tickets", {
          method: "POST",
          body: JSON.stringify({
            projectId: selectedId,
            worldId: map.worldId,
            type: "layout",
            scope: { kind: "global" },
            title: `Build world structure: ${projectName(selectedProject)}`,
            spec: { brief: planBody.summary || selectedProject.description || projectName(selectedProject), worldSizeCm: { x: Number(size.x) * 100, y: Number(size.y) * 100 }, maxCells: Number(planBody.maxSections || 5), maxCommissionsPerCell: 0, referenceImageUrls: [] },
            refVersions: {},
            dependsOn: [],
            createdBy: "studio",
            pool: "agent",
            priority: 7,
            maxAttempts: 3,
            budgetUsdCap: 10,
            dedupSubject: `map-${map.worldId}-world-plan-${plan.approvedRevisionId}`,
          }),
        });
        toast("World structure queued; progress is visible on the Board");
        navigate("tickets");
      } catch (error) {
        button.disabled = false;
        button.textContent = "Generate first greybox";
        toast(`Could not queue greybox: ${String(error)}`, true);
      }
    });
    root.querySelector('[data-studio-action="start-production"]')?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = "Creating area tickets…";
      try {
        const result = await api(`/projects/${encodeURIComponent(selectedId)}/production/start`, { method: "POST" });
        toast(`${result.createdCount || 0} area production ticket${result.createdCount === 1 ? "" : "s"} ready on the Board`);
        await loadProjectDetail();
        render();
      } catch (error) {
        button.disabled = false;
        button.textContent = "Start prop dressing pass";
        toast(`Could not start production: ${String(error)}`, true);
      }
    });
  }

  projectSelect.addEventListener("change", async () => {
    if (projectSelect.value === "__new__") {
      creating = true;
      if (document.body.dataset.view !== "studio") navigate("studio");
      else render();
      return;
    }
    creating = false;
    loadError = "";
    setSelected(projectSelect.value);
    loading = true;
    if (document.body.dataset.view === "studio") renderLoading();
    await loadProjectDetail();
    await updateNeedsCount();
    loading = false;
    if (document.body.dataset.view === "studio") render();
  });

  return {
    render,
    refresh: refreshProjects,
    getProjectId: () => selectedId,
    getProject: () => selectedProject,
  };
}
